import React from 'react';
import { useState, useEffect , useRef} from 'react';
import { Link } from "react-router-dom";
import { Truck, Wifi, Barcode, QrCode, Globe, Cpu, Code, Settings , ArrowRight} from "lucide-react";
import { Eye, Clock, Zap, FileText, Search, Smartphone, GitBranch, TrendingUp } from "lucide-react";
import { motion, useAnimation } from 'framer-motion';
import technology from "../../../Assets/Mobillor/technology.jpg";
import material from "../../../Assets/Mobillor/material.jpg";
import smartManufacturing from "../../../Assets/Mobillor/smart_manufacturing.jpg";
import electricVehicle from "../../../Assets/Mobillor/electric_vehicle.jpg";
import paint from "../../../Assets/Mobillor/paint_manufacture.jpg";
import tyre from "../../../Assets/Mobillor/tyre_manufacture.jpg";
import global from "../../../Assets/Mobillor/global_manufacture.jpg";
import autoComponent from "../../../Assets/Mobillor/auto_component.webp";
import SmartTechnologyImage from '../../../Assets/Mobillor/smart-technology.jpg'
import chipIcon from '../../../Assets/Mobillor/chip-icon3-2.svg';
import { Helmet } from 'react-helmet';

const Mobillor = () => {

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>PTKCS Tech | Mobillor – Mobile Workforce & Field Service Management Solutions</title>
        <meta 
          name="description" 
          content="PTKCS Tech offers comprehensive Mobillor services to optimize mobile workforce and field service operations, improve task management, and enhance real-time communication. Our solutions enable businesses to increase productivity, monitor performance, and streamline field activities efficiently." 
        />
        <meta 
          name="keywords" 
          content="Mobillor, mobile workforce management, field service management, task management, real-time communication, workforce optimization, productivity improvement, performance monitoring, PTKCS Tech, field operations solutions" 
        />
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="PTKCS Tech" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>


    <main className="w-full">
      <BannerSlider/>
            <PieChartSection/>
      <FeaturesSection/>
      <BenefitsSection/>
      <DigitalFrameworks/>
      <ManufacturersGrid/>
      <ContactAndFAQ/>
    </main>
    </>
  );
};


const BannerSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const slides = [
    {
      title: "Mobillor Industry 4.0 Suite",
      subtitle: "Transforming the Manufacturing Industry through Technology and Innovation.",
      description:
        "Embracing the future with the adoption of digital technologies, creating the factories of tomorrow, and achieving seamless shop floor-to-top floor connectivity.",
      image: technology,
    },
    {
      title: "Mobillor Industry 4.0 Suite",
      subtitle: "Enabling end-to-end traceability: Man, Machine, and Material",
      description:
        "Enable backward and forward traceability of material from source to consumer, Task based operations monitoring and equipment effectiveness.",
      image: material,
    },
    {
      title: "Mobillor Industry 4.0 Suite",
      subtitle: "Revolutionizing Smart Manufacturing and Industry 4.0",
      description:
        "Mobillor Industry 4.0 Suite of solutions serve as a bridge connecting the dots from the shop floor to Shipment, enabling both horizontal and vertical integration across the value network.",
      image: smartManufacturing,
    },
  ];

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    if (window.innerWidth < 768) {
      touchStartX.current = e.touches[0].clientX;
    }
  };

  const handleTouchEnd = (e) => {
    if (window.innerWidth < 768) {
      touchEndX.current = e.changedTouches[0].clientX;
      handleSwipe();
    }
  };

  const handleSwipe = () => {
    const swipeThreshold = 50;
    if (!touchStartX.current || !touchEndX.current) return;

    const diff = touchStartX.current - touchEndX.current;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div
      className="relative w-full overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slider Container */}
      <div className="relative h-[600px]">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            {/* Background Image */}
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />

            {/* Gradient Overlay */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to right, rgba(0,0,0,0.8), rgba(0,0,0,0.4), transparent)",
              }}
            ></div>

            {/* Text Content */}
            <div className="absolute inset-0 flex items-center justify-start px-4 md:px-8 lg:px-16">
              <div className="max-w-4xl text-left">
                <h2
                  style={{
                    fontSize: "2rem",
                    fontWeight: "600",
                    color: "red",
                    marginBottom: "1rem",
                  }}
                >
                  {slide.title}
                </h2>
                <h3
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "600",
                    color: "white",
                    marginBottom: "1.5rem",
                  }}
                >
                  {slide.subtitle}
                </h3>
                <p
                  style={{
                    fontSize: "1.125rem",
                    color: "white",
                    opacity: 0.9,
                    lineHeight: "1.6",
                    marginBottom: "2rem",
                  }}
                >
                  {slide.description}
                </p>

                {/* Buttons */}
                <div className="flex gap-4">
                  <Link
                    to="/contact"
                    style={{
                      padding: "0.75rem 2rem",
                      backgroundColor: "#2563eb",
                      color: "white",
                      fontWeight: "500",
                      borderRadius: "0.5rem",
                      transition: "all 0.3s ease",
                      display: "inline-block",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.backgroundColor = "#1d4ed8")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.backgroundColor = "#2563eb")
                    }
                  >
                    Learn More
                  </Link>

                  <Link
                    to="/contact"
                    style={{
                      padding: "0.75rem 2rem",
                      backgroundColor: "transparent",
                      border: "2px solid white",
                      color: "white",
                      fontWeight: "500",
                      borderRadius: "0.5rem",
                      transition: "all 0.3s ease",
                      display: "inline-block",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = "white";
                      e.currentTarget.style.color = "#111827";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "white";
                    }}
                  >
                    Contact Now
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="hidden md:flex absolute left-4 top-1/2 transform -translate-y-1/2 z-30 p-3 rounded-full transition duration-300"
        style={{ backgroundColor: "rgba(0,0,0,0.5)", color: "white" }}
        aria-label="Previous slide"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          style={{ height: "24px", width: "24px" }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button
        onClick={nextSlide}
        className="hidden md:flex absolute right-4 top-1/2 transform -translate-y-1/2 z-30 p-3 rounded-full transition duration-300"
        style={{ backgroundColor: "rgba(0,0,0,0.5)", color: "white" }}
        aria-label="Next slide"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          style={{ height: "24px", width: "24px" }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Indicators */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-30 space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: index === currentSlide ? "white" : "rgba(255,255,255,0.5)",
              transition: "all 0.3s ease",
            }}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};



const PieChartSection = () => {
  const chartData = [
    { percentage: 25, label: "Labour Efficiency", status: "up", color: "#FF8A36" },
    { percentage: 90, label: "Traceability", status: "up", color: "#FF8A36" },
    { percentage: 5, label: "Operational Cost", status: "down", color: "#FF8A36" },
    { percentage: 75, label: "Inventory Accuracy & Visibility", status: "up", color: "#FF8A36" },
    { percentage: 80, label: "Operational Realtime Visibility", status: "up", color: "#FF8A36" }
  ];

  const RADIUS = 45;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  return (
    <div className="py-10 hidden md:block" style={{ backgroundColor: "#2563eb" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 justify-items-center">
          {chartData.map((item, index) => (
            <div key={index} className="flex flex-col items-center text-center">
              {/* Chart Container */}
              <div className="relative w-24 h-24">
                {/* SVG Progress Circle */}
                <svg
                  className="absolute inset-0 -rotate-90"
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Background Circle */}
                  <circle
                    cx="50"
                    cy="50"
                    r={RADIUS}
                    stroke="#ffffff"
                    strokeWidth="6"
                    fill="transparent"
                  />
                  {/* Progress Circle */}
                  <circle
                    cx="50"
                    cy="50"
                    r={RADIUS}
                    stroke={item.color}
                    strokeWidth="4"
                    fill="transparent"
                    strokeDasharray={CIRCUMFERENCE}
                    strokeDashoffset={
                      CIRCUMFERENCE - (item.percentage / 100) * CIRCUMFERENCE
                    }
                    strokeLinecap="round"
                  />
                </svg>

                {/* Inner White Circle */}
                <div
                  className="absolute inset-2 rounded-full flex flex-col items-center justify-center"
                  style={{ backgroundColor: "#2563eb" }}
                >
                  {/* Arrow */}
                  {item.status === "up" ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      style={{ color: "#FF8A36" }}
                    >
                      <path d="M5 10l5-5 5 5H5z" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      style={{ color: "#FF8A36" }}
                    >
                      <path d="M5 10l5 5 5-5H5z" />
                    </svg>
                  )}

                  {/* Percentage */}
                  <div
                    className="font-semibold text-sm"
                    style={{ color: "#ffffff" }}
                  >
                    {item.percentage}%
                  </div>
                </div>
              </div>

              {/* Label */}
              <div
                className="mt-3 text-sm font-medium max-w-[120px] leading-tight"
                style={{ color: "#ffffff" }}
              >
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};



const FeaturesSection = () => {
  const features = [
    {
      id: 1,
      icon: <Truck className="w-8 h-8" style={{ color: "white" }} />,
      title: "Mobility",
      description:
        "Enables real-time data access and remote monitoring, which helps to increase efficiency, improve decision-making, and reduce costs, ultimately optimizing industrial operations.",
    },
    {
      id: 2,
      icon: <Wifi className="w-8 h-8" style={{ color: "white" }} />,
      title: "IoT",
      description:
        "Maximize IoT's potential by connecting, monitoring, and managing devices to drive data-driven decisions and embrace the future of interconnected technologies.",
    },
    {
      id: 3,
      icon: <Barcode className="w-8 h-8" style={{ color: "white" }} />,
      title: "RFID",
      description:
        "Integrate RFID for precise asset tracking, real-time monitoring, and streamlined inventory management, enhancing operational efficiency and productivity.",
    },
    {
      id: 4,
      icon: <QrCode className="w-8 h-8" style={{ color: "white" }} />,
      title: "QR/Bar Code",
      description:
        "Leverage QR Codes and Barcodes to streamline operations, enhance inventory management, and improve traceability within industries for efficient operations.",
    },
    {
      id: 5,
      icon: <Globe className="w-8 h-8" style={{ color: "white" }} />,
      title: "Application Integration",
      description:
        "Ensures smooth integration between Heterogeneous Applications and Data Sources. Seamless integrations with Major ERP Systems like SAP, Oracle, Infor, MS, Epicor, Sage, etc.",
    },
    {
      id: 6,
      icon: <Cpu className="w-8 h-8" style={{ color: "white" }} />,
      title: "Device Integration",
      description:
        "Seamlessly integrates with an extensive array of devices, such as PLCs and machinery, enabling the extraction, analysis, and visualization of operational data.",
    },
    {
      id: 7,
      icon: <Code className="w-8 h-8" style={{ color: "white" }} />,
      title: "Easy to Build and Run",
      description:
        "Low code No code, Flow based development. Easy to run and manage hundreds of developed APIs from a single dashboard.",
    },
    {
      id: 8,
      icon: <Settings className="w-8 h-8" style={{ color: "white" }} />,
      title: "Flexible",
      description:
        "Flexible deployment model. As per the business requirement, customers can choose to go on-premise or cloud of choice.",
    },
  ];

  return (
    <div style={{ backgroundColor: "#ffffff" }} className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2
          className="text-center text-2xl mb-8"
          style={{ color: "#111827" }}
        >
          Efficient, Effective, and Effortless Adoption of Digital Technologies
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature) => (
            <div
              key={feature.id}
              style={{ backgroundColor: "white" }}
              className="rounded-lg shadow-md p-6 flex flex-col items-center"
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: "#3B82F6" }} // blue-500
              >
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
              <p className="text-center" style={{ color: "#4B5563" }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};



const BenefitsSection = () => {
  const benefits = [
  {
    id: 1,
    icon: <Eye className="w-6 h-6" />,
    title: "Transparent",
    description:
      "Enhancing real-time visibility of operational data across the plant, Shop Floor to Top Floor for right decision-making.",
    color: "#00BFFF", // Electric Blue – vibrant, futuristic
    textColor: "#005F9D",
    bgColor: "rgba(0, 187, 255, 0.1)",
    gradient: "linear-gradient(135deg, #00BFFF, #0088FF)"
  },
  {
    id: 2,
    icon: <Clock className="w-6 h-6" />,
    title: "Proactive",
    description:
      "Facilitating proactive decision-making by ensuring real-time access to accurate data.",
    color: "#FF4081", // Hot Pink – energetic, attention-grabbing
    textColor: "#C2185B",
    bgColor: "rgba(255, 64, 129, 0.1)",
    gradient: "linear-gradient(135deg, #FF4081, #E91E63)"
  },
  {
    id: 3,
    icon: <Zap className="w-6 h-6" />,
    title: "Connected",
    description:
      "Cultivates a real-time connected ecosystem of Man, Machine, and Material.",
    color: "#4CAF50", // Emerald Green – strong, trustworthy
    textColor: "#2E7D32",
    bgColor: "rgba(76, 175, 80, 0.1)",
    gradient: "linear-gradient(135deg, #4CAF50, #388E3C)"
  },
  {
    id: 4,
    icon: <FileText className="w-6 h-6" />,
    title: "Paperless",
    description:
      "Operations are completely digitalized with zero paper involvement. Reducing possibilities of Human Errors.",
    color: "#00BCD4", // Cyan Blue – clean, techy
    textColor: "#008BA0",
    bgColor: "rgba(0, 188, 212, 0.1)",
    gradient: "linear-gradient(135deg, #00BCD4, #0096C7)"
  },
  {
    id: 5,
    icon: <Search className="w-6 h-6" />,
    title: "Perspective",
    description:
      "Utilizing digitalization in industries widens perspective, providing a comprehensive and operational view.",
    color: "#F44336", // Red Alert – bold, urgent
    textColor: "#B71C1C",
    bgColor: "rgba(244, 67, 54, 0.1)",
    gradient: "linear-gradient(135deg, #F44336, #D32F2F)"
  },
  {
    id: 6,
    icon: <Smartphone className="w-6 h-6" />,
    title: "Mobile",
    description:
      "Enables mobile accessibility and shop floor to top floor connectivity, empowering remote operations and optimizing workflow efficiency.",
    color: "#FFC107", // Amber Yellow – warm, energetic
    textColor: "#E6A200",
    bgColor: "rgba(255, 193, 7, 0.1)",
    gradient: "linear-gradient(135deg, #FFC107, #FFA000)"
  },
  {
    id: 7,
    icon: <GitBranch className="w-6 h-6" />,
    title: "Flexible",
    description:
      "Available on cloud, on-premise, hybrid, cloud & database of choice.",
    color: "#9C27B0", // Deep Purple – creative, modern
    textColor: "#6A1B9A",
    bgColor: "rgba(156, 39, 176, 0.1)",
    gradient: "linear-gradient(135deg, #9C27B0, #7B1FA2)"
  },
  {
    id: 8,
    icon: <TrendingUp className="w-6 h-6" />,
    title: "Faster",
    description:
      "Accelerates processes for faster decisions, enhancing productivity and resource efficiency.",
    color: "#4CAF50", // Reused green for consistency
    textColor: "#2E7D32",
    bgColor: "rgba(76, 175, 80, 0.1)",
    gradient: "linear-gradient(135deg, #4CAF50, #388E3C)"
  },
];


  const controls = useAnimation();

  useEffect(() => {
    controls.start({
      backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
      transition: {
        duration: 15,
        ease: "linear",
        repeat: Infinity
      }
    });
  }, [controls]);

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Animated background gradient */}
      <motion.div 
        className="absolute inset-0 opacity-20"
        style={{
          // background: `linear-gradient(120deg, #3B82F6, #8B5CF6, #EC4899, #F59E0B, #10B981)`,
          backgroundColor:'#ffffff',
          backgroundSize: "400% 400%"
        }}
        animate={controls}
      />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6" style={{ 
            background: 'linear-gradient(135deg, #1e293b, #3b82f6, #1e293b)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            What are the benefits?
          </h2>
          <p className="text-xl md:text-2xl text-slate-600 max-w-4xl mx-auto leading-relaxed mb-10">
            The technology and suite of solutions provided by Mobillor for Industry 4.0 and Smart Manufacturing assist manufacturing and distribution companies in attaining noteworthy benefits, such as:
          </p>
          
          <motion.a
            href="/contact"
            className="inline-block font-bold py-4 px-8 rounded-full text-white text-lg shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)'
            }}
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            }}
            whileTap={{ scale: 0.95 }}
          >
            KNOW MORE
          </motion.a>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Benefits Cards */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ 
                    y: -10,
                    scale: 1.02,
                    transition: { duration: 0.3 }
                  }}
                  className="rounded-2xl p-8 backdrop-blur-sm border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden"
                  style={{ backgroundColor: benefit.bgColor }}
                >
                  <div className="flex items-start gap-5">
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 text-white shadow-lg"
                      style={{ backgroundColor: benefit.color }}
                    >
                      {benefit.icon}
                    </div>
                    <div className="flex-1">
                      <h3 
                        className="text-2xl font-bold mb-3"
                        style={{ color: benefit.textColor }}
                      >
                        {benefit.title}
                      </h3>
                      <p className="text-slate-700 leading-relaxed text-base">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Decorative element */}
                  <div 
                    className="absolute bottom-0 left-0 right-0 h-1 opacity-30 rounded-full"
                    style={{ backgroundColor: benefit.color }}
                  ></div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Visual Showcase */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            whileHover={{ 
              y: -5,
              transition: { duration: 0.3 }
            }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-white/20 backdrop-blur-sm">
              <div className="relative overflow-hidden">
                <img
                  src={SmartTechnologyImage}
                  alt="Industrial Control System"
                  className="w-full h-64 object-cover transform hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              
              <div className="p-8">
                <h3 className="text-2xl font-bold text-slate-800 mb-4">Smart Technology Integration</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Advanced Industry 4.0 solutions that transform traditional manufacturing into intelligent, connected, and efficient operations.
                </p>
                
                <div className="space-y-3">
                  {['Real-time Analytics', 'Predictive Maintenance', 'Digital Twin', 'AI Optimization'].map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + index * 0.1 }}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors duration-200"
                    >
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: '#3B82F6' }}
                      ></div>
                      <span className="text-slate-700 font-medium">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};




const SECTIONS = [
  {
    id: 'supplier',
    title: 'DIGITAL SUPPLIER',
    description:
      'Mobillor Digital Supplier stands as a catalyst for transformation, reimagining the dynamics between manufacturers and their suppliers. With its array of features and seamless integration capabilities, it paves the way for an efficient, transparent, and collaborative supplier engagement ecosystem, elevating manufacturing operations to unprecedented levels of excellence.',
    stats: [
      { percentage: 80, label: 'Receivable visibility', status: 'up' },
      { percentage: 95, label: 'Data entry', status: 'down' },
      { percentage: 100, label: 'Real time & paperless communication', status: 'up' },
      { percentage: 90, label: 'Dispatch Errors', status: 'down' }
    ],
    link: '#contact'
  },
  {
    id: 'warehouse',
    title: 'DIGITAL WAREHOUSE',
    description:
      'Mobillor Digital Warehouse emerges as a trailblazer in the realm of warehouse management. Leveraging the latest principles of Smart Manufacturing and Digitalization, Mobillor Digital Warehouse reshapes the landscape of warehouse operations, ushering in a new era of efficiency and precision.',
    stats: [
      { percentage: 75, label: 'Material Traceability', status: 'up' },
      { percentage: 80, label: 'Pickup Accuracy', status: 'up' },
      { percentage: 20, label: 'Workforce Efficiency', status: 'up' },
      { percentage: 80, label: 'Realtime Visibility', status: 'up' }
    ],
    link: '#contact'
  },
  {
    id: 'manufacturing',
    title: 'DIGITAL MANUFACTURING',
    description:
      'Mobillor Digital Manufacturing stands at the forefront of a new era in production excellence. Built upon the latest principles of Smart Manufacturing and Digitalization, Mobillor Digital Manufacturing transforms the shop floor into a hub of efficiency, collaboration, and seamless operations.',
    stats: [
      { percentage: 75, label: 'WIP Material Traceability', status: 'up' },
      { percentage: 70, label: 'Realtime operations Visibility', status: 'up' },
      { percentage: 85, label: 'Production Data Accuracy', status: 'up' },
      { percentage: 60, label: 'Planning Effectiveness', status: 'up' }
    ],
    link: '#contact'
  },
  {
    id: 'services',
    title: 'DIGITAL SERVICES',
    description:
      'Mobillor Digital Service emerges as a game-changer in the realm of maintenance operations. Mobillor Digital Service reimagines maintenance practices, elevating them to new levels of efficiency and precision. Maintenance operations are at the heart of factory performance, and Mobillor Digital Service transforms the way they are managed.',
    stats: [
      { percentage: 35, label: 'Incident Report Time', status: 'down' },
      { percentage: 80, label: 'Realtime Operation Visibility', status: 'up' },
      { percentage: 75, label: 'Incident Prevention', status: 'up' },
      { percentage: 5, label: 'Machine Life Span', status: 'up' }
    ],
    link: '#contact'
  },
  {
    id: 'distribution',
    title: 'DIGITAL DISTRIBUTION',
    description:
      'Mobillor Digital Distribution – a catalyst for reshaping supply chain dynamics. Designed to transcend the boundaries of an organization, Mobillor Digital Distribution extends its prowess to distributors and dealers, forging seamless collaboration and unprecedented traceability.',
    stats: [
      { percentage: 10, label: 'Warehousing Cost', status: 'up' },
      { percentage: 75, label: 'Secondary Sales Visibility', status: 'up' },
      { percentage: 80, label: 'Channel Stock Visibility', status: 'up' },
      { percentage: 90, label: 'Material Traceability', status: 'up' }
    ],
    link: '#contact'
  }
];

const STEPS = [
  { id: 'supplier', label: 'SUPPLIER' },
  { id: 'warehouse', label: 'WAREHOUSE' },
  { id: 'manufacturing', label: 'MANUFACTURING' },
  { id: 'services', label: 'SERVICES' },
  { id: 'distribution', label: 'DISTRIBUTION' }
];

const DigitalFrameworks = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRefs = useRef([]);
  const stepsRef = useRef(null);

  // Setup IntersectionObserver to update active index and reveal animations
  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, SECTIONS.length);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Number(entry.target.getAttribute('data-idx'));
          if (entry.isIntersecting) {
            setActiveIndex(idx);
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-6');
          } else {
            // keep sections visible when scrolled past upward, but remove when fully out
            if (entry.boundingClientRect.top > window.innerHeight || entry.boundingClientRect.bottom < 0) {
              entry.target.classList.remove('opacity-100', 'translate-y-0');
              entry.target.classList.add('opacity-0', 'translate-y-6');
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '-20% 0px -40% 0px',
        threshold: 0
      }
    );

    sectionRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Smooth scroll to section on step click
  const scrollToSection = (idx) => {
    const el = sectionRefs.current[idx];
    if (!el) return;
    window.scrollTo({ top: el.offsetTop - 120, behavior: 'smooth' });
    // small delay to ensure highlight updates
    setTimeout(() => setActiveIndex(idx), 300);
  };

  return (
    <section className="bg-white">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-12 pb-4">
        <h2 className="text-3xl md:text-4xl font-light text-center tracking-tight mb-2">
          Empowering and Transforming Industries through Innovation
        </h2>
        <p className="text-center font-bold text-gray-600 mt-1">Mobillor Digital Frameworks</p>
      </div>

      {/* Sticky process steps */}
      <div className="sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 hidden md:block">
          <div className="flex items-center gap-6 justify-between bg-white py-4 shadow-sm rounded-md px-4">
            {/* chip icon on left (visible on md+) */}
            <div className="hidden md:flex items-center gap-4">
              <img src={chipIcon} alt="chip" className="w-14 h-14" />
            </div>

            {/* steps bar */}
            <div ref={stepsRef} className="flex-1">
              <div className="flex justify-between items-center space-x-4">
                {STEPS.map((step, idx) => {
                  const isActive = idx === activeIndex;
                  return (
                    <button
                      key={step.id}
                      onClick={() => scrollToSection(idx)}
                      className={`relative flex-1 text-sm md:text-base py-3 px-3 rounded transition-all duration-200 focus:outline-none`}
                        style={{
                        backgroundColor: isActive ? "#2563eb" : "#ffffff", // Tailwind bg-blue-600 ≈ #2563eb
                        color: isActive ? "#ffffff" : "#374151",          // Tailwind text-gray-700 ≈ #374151
                        boxShadow: isActive ? "0 4px 6px rgba(0, 0, 0, 0.1)" : "none", // shadow-md
                        }}

                      aria-pressed={isActive}
                      aria-label={`Go to ${step.label}`}
                    >
                      <div className="flex items-center justify-center">
                        <span className="font-medium tracking-wide">{step.label}</span>
                      </div>
                      {/* triangle connector when active and not last */}
                      {isActive && idx < STEPS.length - 1 && (
                        <span className="absolute right-[-18px] top-1/2 transform -translate-y-1/2">
                          <svg width="36" height="24" viewBox="0 0 36 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 12 L26 12 L18 0 Z" fill="#2563EB" />
                          </svg>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-20 mt-5">
        {SECTIONS.map((section, idx) => (
          <article
            key={section.id}
            data-idx={idx}
            ref={(el) => (sectionRefs.current[idx] = el)}
            className={`model-section mb-20 opacity-0 translate-y-6 transition-all duration-700 ease-out`}
          >
            <div className="flex items-start justify-between">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-800 tracking-wide">
                {section.title}
              </h3>
              <Link to="/contact"  rel="noreferrer" className="ml-4 text-blue-600 hover:text-blue-800">
                <ArrowRight size={24} strokeWidth={2}/>
              </Link>
            </div>

            <p className="text-gray-600 mt-6 max-w-4xl leading-relaxed">{section.description}</p>

            {/* Stats - pie charts */}
            <div className="mt-10 border-t pt-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-start">
                {section.stats.map((stat, sidx) => (
                  <div key={sidx} className="flex flex-col items-center text-center">
                    <div
                      className="w-28 h-28 rounded-full flex items-center justify-center relative shadow-sm"
                      style={{
                        background: `conic-gradient(#fff 0% ${stat.percentage}%, #006AFF ${stat.percentage}% 100%)`,
                        border: '4px solid #1871EF'
                      }}
                    >
                      {/* inner white circle */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div style={{ width: '78%', height: '78%', borderRadius: '50%', background: '#ffffff', border: '4px solid #006AFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <div className="flex flex-col items-center">
                            {/* Arrow Symbol */}
                            {stat.status === 'up' ? (
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                className="h-6 w-6 text-orange-500"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M5 10l5-5 5 5H5z" />
                              </svg>
                            ) : (
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                className="h-6 w-6 text-orange-500 rotate-180"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M5 10l5-5 5 5H5z" />
                              </svg>
                            )}
                            {/* Percentage */}
                            <div className={`text-sm font-semibold mt-1 ${stat.status === 'down' ? 'text-gray-700' : 'text-gray-700'}`}>
                              {stat.percentage}%
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 text-gray-500 text-sm max-w-xs">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

const ManufacturersGrid = () => {
  const manufacturers = [
    {
      id: "electric_vehicle",
      name: "Electric Vehicle Manufacturer",
      backgroundImage: electricVehicle,
    },
    {
      id: "global_equipment",
      name: "Global Fortune 100 Equipment Manufacturer",
      backgroundImage: global,
    },
    {
      id: "paint_manufacturer",
      name: "Global Paint Manufacturer",
      backgroundImage: paint,
    },
    {
      id: "auto_component",
      name: "Global Auto Component Manufacturer",
      backgroundImage: autoComponent,
    },
    {
      id: "tyre_manufacturer",
      name: "Global Tyre Manufacturer",
      backgroundImage: tyre,
    },
  ];

  return (
    <div style={{ backgroundColor: "black", color: "white" }} className="py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {manufacturers.map((manufacturer) => (
            <div
              key={manufacturer.id}
              className="relative bg-cover bg-center h-64 rounded-lg overflow-hidden group"
              style={{ backgroundImage: `url(${manufacturer.backgroundImage})` }}
            >
              {/* Overlay */}
              <div
                className="absolute inset-0 transition-all duration-300 group-hover:bg-opacity-70"
                style={{ backgroundColor: "black", opacity: 0.5 }}
              ></div>

              {/* Content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
                <h2
                  className="text-xl font-bold mb-2"
                  style={{ color: "white" }}
                >
                  {manufacturer.name}
                </h2>
                <div
                  className="w-full max-w-[200px] h-2 mt-2"
                  style={{ backgroundColor: "red" }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};






const ContactAndFAQ = () => {
  const faqs = [
    {
      question:
        "Can Mobillor’s Digital Frameworks be customized and personalized to business requirement?",
      answer:
        "Mobillor’s Digital Frameworks are built on micro services architecture. Each microservice has the possibility of personalizing and customizing to customer specific requirements and business needs.",
    },
    {
      question:
        "What is the deployment flexibility of the Mobillor Industry 4.0 solutions?",
      answer:
        "Mobillor offers deployment flexibility to the customers. Customers can decide to deploy on-premise or on the cloud, further, they can choose the cloud environment of their choice (Azure, AWS, etc.)",
    },
    {
      question:
        "Can the Mobillor’s Industry 4.0 Suite be integrated with SCADA and MES systems?",
      answer:
        "Yes, Mobillor’s Industry 4.0 platform can be integrated with all kinds of MES/SCADA systems, enhancing automation and data exchange.",
    },
    {
      question:
        "Are Mobillor Industry 4.0 Suite of solutions available for any specific industry verticals ?",
      answer:
        "Mobillor Industry 4.0 Suite of solutions are available various verticals within the manufacturing industry. Currently Mobillor is being used by customers in diversified industry verticals such as Automotive OEM, Auto Component Manufacturing, Electric Vehicles, Electronics, Equipment Manufacturing, Packaging, Pharmaceutical, Speciality Chemicals, FMCG, Consumer Goods etc.",
    },
    {
      question: "Can end-to-end traceability be achieved with Mobillor Solutions?",
      answer:
        "Mobillor Suite of solutions helps customers to achieve end-to-end traceability from source to consumer. Traceability is enabled using QR codes, Barcodes, and RFID kind of technologies.",
    },
    {
      question:
        "Can Mobillor Industry 4.0 Suite of solutions seamlessly integrate with ERP systems?",
      answer:
        "ERP systems are the backbone of processes for any organization. Mobillor offers integration possibilities and adaptors for various ERP systems such as SAP, Oracle, Infor, Sage, Epicor, MS Dynamics, Ramco, etc.",
    },
  ];

  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div
      style={{
        background: "linear-gradient(to bottom, white, #ffe9e3)",
        paddingTop: "4rem",
        paddingBottom: "4rem",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* Contact Section */}
        <div style={{ padding: "2rem" }}>
          <h2
            style={{
              fontSize: "1.875rem",
              fontWeight: "bold",
              color: "#2d3748",
              marginBottom: "1rem",
            }}
          >
            READY FOR A DIGITAL JOURNEY?
          </h2>
          <p style={{ color: "#4a5568", marginBottom: "0.5rem" }}>
            Explore how Mobillor’s Industry 4.0 Suite of Solutions can help you
            in your Digital journey toward transformation for the future.
          </p>
          <p style={{ color: "#4a5568", marginBottom: "0.5rem" }}>Reach us now.</p>
          <p style={{ color: "#4a5568", marginBottom: "0.5rem" }}>
            +91(0) 120 6948681
          </p>
          <a
            href="mailto:info@mobillor.com"
            style={{ color: "#2563eb", textDecoration: "underline", marginBottom: "1.5rem", display: "block" }}
          >
            info@mobillor.com
          </a>
          <Link
            to="/contact"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.75rem 1.5rem",
              borderRadius: "0.375rem",
              backgroundColor: "#2563eb",
              color: "white",
              fontWeight: "500",
              transition: "background-color 0.3s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#1e40af")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#2563eb")}
          >
            CONTACT NOW
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="ml-2"
              width="18"
              height="18"
              fill="none"
              viewBox="0 0 22 22"
              style={{ marginLeft: "0.5rem" }}
            >
              <path
                d="M4.71233 1.99976H20M20 1.99976V16.3011M20 1.99976L2 19.9998"
                stroke="white"
                strokeWidth="4"
              ></path>
            </svg>
          </Link>
        </div>

        {/* FAQ Section */}
        <div className="flex flex-col gap-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "0.5rem",
                backgroundColor: "white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                overflow: "hidden",
              }}
            >
              <div
                onClick={() => toggleFAQ(index)}
                style={{
                  padding: "1.25rem",
                  cursor: "pointer",
                  backgroundColor: activeIndex === index ? "white" : "white",
                }}
                onMouseEnter={(e) => {
                  if (activeIndex !== index) e.currentTarget.style.backgroundColor = "#f9fafb";
                }}
                onMouseLeave={(e) => {
                  if (activeIndex !== index) e.currentTarget.style.backgroundColor = "white";
                }}
              >
                <h3 style={{ color: "#2d3748", fontWeight: "500" }}>{faq.question}</h3>
              </div>
              <div
                style={{
                  maxHeight: activeIndex === index ? "400px" : "0px",
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  padding: activeIndex === index ? "1.25rem" : "0rem",
                }}
              >
                <p style={{ color: "#4a5568" }}>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


export default Mobillor;